import React, { useCallback, useMemo } from 'react';
import Animated from 'react-native-reanimated';
import { AppView, Button, rem, SCROLL_INDICATOR_INSETS, withAnimatedNavigationHeader, } from 'design-system';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import { useAppSelector } from 'store/hooks';
import { selectSpacePots } from 'features/pots/reducers/selectors';
import { selectSpaceTradingAccounts } from 'reducers/selectors';
import sortAccounts from 'utils/api/sortAccounts';
import SectionHeader from 'design-system/SectionHeader';
import FlatListItemCard from 'design-system/FlatListItemCard';
import createStyleSheets from 'utils/createStyleSheets';
import useStyles from 'hooks/useStyles';
import Text from 'design-system/Text';
import ListEmptyView from 'design-system/ListEmptyView';
import { useMarginBottom } from 'hooks/useMarginBottom';
import { HEIGHT_PLUS_PADDING } from 'design-system/Button';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { truncate } from 'lodash';
import SpaceMemberItem from './containers/SpaceMemberItem';
import { AccountItem } from './components';
import { selectFetchingMySpaces } from './reducers/selectors';
import { useSpaceInfo } from './hooks';
import { isWeb } from '../../constants';
const JoinedSpaceScreen = withAnimatedNavigationHeader(({ route, navigation, scrollHandler }) => {
    const { space } = route.params;
    const { id, createdBy } = space;
    const styles = useStyles(styleSet);
    const [spaceInfo] = useSpaceInfo(id);
    const pots = useAppSelector(selectSpacePots)?.[id];
    const tradingAccounts = useAppSelector(selectSpaceTradingAccounts)?.[id];
    const fetchingMySpaces = useAppSelector(selectFetchingMySpaces);
    const data = useMemo(() => {
        const listData = [];
        if (!spaceInfo || fetchingMySpaces)
            return [];
        if (spaceInfo.users?.length) {
            listData.push({
                type: 'SECTION_HEADER',
                sectionTitle: 'Members',
            });
            spaceInfo.users.forEach((data, index, arr) => listData.push({
                type: 'MEMBER',
                data,
                index,
                length: arr.length,
            }));
        }
        listData.push({
            type: 'SECTION_HEADER',
            sectionTitle: 'Accounts',
        });
        if (spaceInfo.space.accountsCount) {
            listData.push({
                type: 'SECTION_DESC',
                sectionDesc: 'Balance and transactions of these accounts are shared in this space',
            });
            const sorted = sortAccounts([
                ...(spaceInfo.accounts || []),
                ...(pots || []),
                ...(tradingAccounts || []),
            ]);
            sorted.forEach((data, index, arr) => listData.push({ type: 'ACCOUNTS', data, index, length: arr.length }));
        }
        else {
            listData.push({ type: 'ACCOUNTS_EMPTY' });
        }
        return listData;
    }, [fetchingMySpaces, pots, spaceInfo, tradingAccounts]);
    const ListHeaderComponent = useMemo(() => (<ScreenInfoHeader title="This is the space you have joined" style={styles.header}/>), [styles.header]);
    const renderItem = useCallback(({ item }) => {
        if (item.type === 'SECTION_HEADER') {
            return (<SectionHeader horizontalMargins largeVerticalMargins>
              {item.sectionTitle}
            </SectionHeader>);
        }
        if (item.type === 'SECTION_DESC') {
            return (<Text TextThin-16 Secondary style={styles.desc}>
              {item.sectionDesc}
            </Text>);
        }
        if (item.type === 'MEMBER') {
            return (<FlatListItemCard index={item.index} itemsLength={item.length} style={styles.card}>
              <SpaceMemberItem spaceId={id} ownerId={createdBy} member={item.data} disabled/>
            </FlatListItemCard>);
        }
        if (item.type === 'ACCOUNTS') {
            return (<FlatListItemCard index={item.index} itemsLength={item.length} style={styles.card}>
              <AccountItem account={item.data} disabled/>
            </FlatListItemCard>);
        }
        if (item.type === 'ACCOUNTS_EMPTY') {
            return (<ListEmptyView onCard compact title="No accounts" subTitle="This space does not have any accounts added to it."/>);
        }
        return null;
    }, [createdBy, id, styles.card, styles.desc]);
    const onContinue = useCallback(() => {
        navigation.navigateDeprecated('JoinedSpaceAddAccounts', {
            spaceInfo: spaceInfo ?? { space },
        });
    }, [space, spaceInfo]);
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const contentContainerStyle = useMarginBottom('paddingBottom', HEIGHT_PLUS_PADDING, paddingHorizontalStyle);
    return (<AppView withFrame={false}>
        <Animated.FlatList data={data} renderItem={renderItem} onScroll={scrollHandler} scrollEventThrottle={16} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS} ListHeaderComponent={ListHeaderComponent} contentContainerStyle={contentContainerStyle}/>
        <Button brand floating title="Continue" onPress={onContinue}/>
      </AppView>);
}, ({ colors, route }) => ({
    title: `${truncate(`${route.params.space.name} space`, { length: 24 })}`,
    hideTitleOnScroll: true,
    headerColorOutputRange: [
        colors.background.dark,
        colors.cards.onDark,
    ],
}));
const styleSet = createStyleSheets((colors) => ({
    header: {
        paddingBottom: rem(8),
    },
    sectionTitle: {
        marginTop: rem(24),
    },
    desc: {
        marginHorizontal: rem(16),
        marginBottom: rem(16),
    },
    card: {
        backgroundColor: colors.cards.onDark,
    },
}));
export default JoinedSpaceScreen;
